import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { removeEventMutation } from '../../apollo/events';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
};

const DeleteEventDialog = ({ open, onClose, eventId }: Props) => {
  const router = useRouter();
  const [removeEvent] = useMutation(removeEventMutation);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = useCallback(() => {
    setSubmitting(true);
    removeEvent({
      variables: { id: eventId },
    }).then(() => {
      onClose();
      router.push('/calendar');
    }).finally(() => {
      setSubmitting(false);
    });
  }, [eventId, removeEvent, onClose, router]);

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [submitting, onClose]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Please Confirm</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this event?
        </DialogContentText>
        <DialogContentText>
          Attendees will not be notified. If you&lsquo;d like to notify attendees, choose Cancel Event instead.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" disabled={submitting} autoFocus>
          {submitting ? 'Deleting...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteEventDialog;
